import type {
  SearchFlowClassificationRequest,
  SearchFlowClassificationResponse,
  SearchHistoryResponse,
  KnowledgeSearchOutput,
  KnowledgeSource,
} from "@/shared/lib/search/interfaces";

// ============================================================================
// Knowledge Query API (CR-013) — row-based search over synced connector data.
// Moved from ui/web/src/ee/lib/search/svc.ts (SOL-013 §3.1/§3.3): Search UI is
// core Phase-1, not an EE add-on. `searchDocuments()` (chat-document-search,
// POST /api/search/send-search-message) is dropped — QueryControllerProvider
// now calls `searchKnowledge()` instead. `classifyQuery()`/`fetchSearchHistory()`
// are kept as-is (server-side, unrelated to the row-search response shape).
// ============================================================================

/** Phân loại câu hỏi search-vs-chat cho "auto" mode. KHÔNG đổi — không phụ
 *  thuộc response shape của kết quả search (đó là searchKnowledge bên dưới). */
export async function classifyQuery(
  query: string,
  signal?: AbortSignal
): Promise<SearchFlowClassificationResponse> {
  const response = await fetch("/api/search/search-flow-classification", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_query: query,
    } as SearchFlowClassificationRequest),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Classification failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Row-based search trên dữ liệu connector đã sync (SRCH-01/02, backend
 * knowledge-service GET /api/v1/knowledge/search — xem
 * backend/services/knowledge-service/internal/presentation/http/handler/search_handler.go).
 * THAY THẾ searchDocuments() — chat-document-search flow không còn dùng ở đây.
 *
 * Lưu ý đã xác nhận qua đọc source backend: handler hiện chỉ đọc `q`, `source`,
 * `limit`, `page` — KHÔNG đọc `owner`/`type`/`from`/`to`. FE vẫn gửi các param
 * này (mở rộng hợp đồng, SOL-013 §6.5) nhưng chúng sẽ no-op ở server cho tới
 * khi backend bổ sung — không phải lỗi FE.
 */
export async function searchKnowledge(
  query: string,
  options?: {
    source?: string;
    owner?: string;
    type?: string;
    from?: string;
    to?: string;
    limit?: number;
    page?: number;
    signal?: AbortSignal;
  }
): Promise<KnowledgeSearchOutput> {
  const params = new URLSearchParams({ q: query });
  if (options?.source) params.set("source", options.source);
  if (options?.owner) params.set("owner", options.owner);
  if (options?.type) params.set("type", options.type);
  if (options?.from) params.set("from", options.from);
  if (options?.to) params.set("to", options.to);
  params.set("limit", String(options?.limit ?? 20));
  params.set("page", String(options?.page ?? 1));

  const response = await fetch(`/api/v1/knowledge/search?${params.toString()}`, {
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new Error(`Search failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Server-side source facet cho filter panel (SOL-013 §3.5). Backend wraps the
 * array in `{"data": [...]}` (commonresp.OK — xem
 * backend/services/knowledge-service/internal/presentation/http/handler/knowledge_handler.go
 * ListSources) — đã xác nhận qua đọc source, không phải giả định.
 */
export async function fetchKnowledgeSources(signal?: AbortSignal): Promise<KnowledgeSource[]> {
  const response = await fetch("/api/v1/knowledge/sources", { signal });

  if (!response.ok) {
    throw new Error(`Failed to fetch sources: ${response.statusText}`);
  }

  const body = await response.json();
  return body.data;
}

/**
 * Fetch search history for the current user. Server-side variant — audit/
 * cross-device, KHÔNG bị thay bởi localStorage (SOL-013 §3.7). KHÔNG đổi.
 */
export async function fetchSearchHistory(options?: {
  limit?: number;
  filterDays?: number;
  signal?: AbortSignal;
}): Promise<SearchHistoryResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", options.limit.toString());
  if (options?.filterDays)
    params.set("filter_days", options.filterDays.toString());

  const response = await fetch(
    `/api/search/search-history?${params.toString()}`,
    {
      signal: options?.signal,
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch search history: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// Knowledge Document Content API (CR-031) — unrelated to search, kept as-is
// (this file already existed with this content from a concurrent session's
// work; merged rather than overwritten to avoid clobbering it).
// ============================================================================

const DOCUMENT_CONTENT_PREFIX = "/api/v1/documents";

export interface SignedUrlContentResponse {
  signed_url: string;
  expires_at: string;
  mime_type: string;
  size_bytes: number;
}

export interface TextContentResponse {
  content: string;
  mime_type: string;
  truncated: boolean;
}

/**
 * Fetch a Knowledge Base document's content by its ID, returning the raw
 * Response. Mirrors fetchChatFile (ui/web/src/lib/chat/svc.ts) but targets
 * knowledge-service's GET /api/v1/documents/:id/content instead of
 * ai-service's GET /api/chat/file/:id — CR-031.
 *
 * Caller must check `response.headers.get("Content-Type")` before consuming
 * the body:
 *   - "application/json" with `format=raw` → SignedUrlContentResponse (document
 *     exceeded the 25MB inline threshold), NOT a blob.
 *   - "application/json" with `format=text` → TextContentResponse.
 *   - anything else with `format=raw` → the raw file blob, same shape as
 *     fetchChatFile's response.
 */
export async function fetchKnowledgeDocumentContent(
  documentId: string,
  format: "raw" | "text" = "raw"
): Promise<Response> {
  const response = await fetch(
    `${DOCUMENT_CONTENT_PREFIX}/${encodeURIComponent(documentId)}/content?format=${format}`,
    { method: "GET", cache: "force-cache" }
  );

  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "Document has no stored preview content."
        : "Failed to load document."
    );
  }

  return response;
}
